import {
  AUCTION_DURATION_MS,
  AUCTION_FEE_RATE,
  AUCTION_MAX_LISTINGS,
  AUCTION_MAX_PRICE,
  auctionFee,
  type BagEntry,
  getItem,
  type ItemTemplate,
  sellerProceeds,
} from '@shefira/shared';

import { listItem } from '../net/auction.js';
import { type AuctionDesk, failureText } from './auction-format.js';
import { coin, formatGold, itemRow, markIcon, stepper, upgradedName, workAction } from './bench.js';
import { describeOnHover } from './item-hover.js';
import { uiFormat, uiNumber, uiPercent, uiText } from './text.js';

/**
 * The sale form of « Vendre » (ADR 0035): the item picked, how many of the
 * stack, the price of the whole lot, and what the house keeps of it
 * (`auctionFee`) against what comes back (`sellerProceeds`), before the
 * button that puts it up. The server checks it all again.
 */

export interface AuctionSellFormOptions {
  activeLots: () => number;
  onBack: () => void;
  onListed: () => void;
}

/** The schema's own bound on a lot's quantity (`AuctionListRequestSchema`). */
const MAX_QUANTITY = 99;

export class AuctionSellForm {
  readonly root: HTMLElement;
  private readonly card: HTMLElement;
  private readonly quantityField: HTMLElement;
  private readonly quantityBox: HTMLElement;
  private readonly price: HTMLInputElement;
  private readonly each: HTMLElement;
  private readonly summary: HTMLElement;
  private readonly work: HTMLElement;
  private entry: BagEntry | undefined;
  private count = 1;
  private busy = false;

  constructor(
    private readonly desk: AuctionDesk,
    private readonly options: AuctionSellFormOptions,
  ) {
    this.root = document.createElement('div');
    this.root.className = 'auction__form';
    this.card = document.createElement('div');

    const fields = document.createElement('div');
    fields.className = 'auction__fields';
    this.quantityField = document.createElement('div');
    this.quantityField.className = 'auction__field';
    this.quantityBox = document.createElement('div');
    this.quantityField.append(this.caption('Quantité'), this.quantityBox);

    const priceField = document.createElement('label');
    priceField.className = 'auction__field';
    const priceBox = document.createElement('span');
    priceBox.className = 'auction__amount';
    this.price = document.createElement('input');
    this.price.className = 'auction__input auction__price-input';
    this.price.type = 'number';
    this.price.inputMode = 'numeric';
    this.price.min = '1';
    this.price.max = String(AUCTION_MAX_PRICE);
    this.price.step = '1';
    this.price.placeholder = '0';
    this.price.addEventListener('input', () => {
      this.renderSummary();
    });
    priceBox.append(coin(), this.price);
    this.each = document.createElement('span');
    this.each.className = 'auction__each';
    priceField.append(this.caption('Prix du lot'), priceBox, this.each);
    fields.append(this.quantityField, priceField);

    this.summary = document.createElement('div');
    this.summary.className = 'auction__summary';
    this.work = document.createElement('div');
    const note = document.createElement('p');
    note.className = 'bench__hint auction__note';
    note.textContent = uiFormat('Le lot reste en vente {hours} h ; invendu, tu le récupères ici.', {
      hours: Math.round(AUCTION_DURATION_MS / 3_600_000),
    });

    this.root.append(this.card, fields, this.summary, this.work, note);
    // Typing a price must not fire the game's shortcuts; Escape leaves the
    // field, Enter puts the lot up.
    this.root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && event.target instanceof HTMLElement) event.target.blur();
      if (event.key === 'Enter' && event.target === this.price) this.sell();
      event.stopPropagation();
    });
  }

  /** Shows an item, keeping the price typed so far when it is the same one (the bag was pushed again). */
  show(entry: BagEntry, template: ItemTemplate): void {
    if (this.entry?.item.id !== entry.item.id) {
      this.price.value = '';
      this.count = 1;
    }
    this.entry = entry;
    this.count = Math.min(this.count, this.most());
    this.renderCard(template);
    this.renderQuantity();
    this.renderSummary();
  }

  reset(): void {
    this.entry = undefined;
    this.price.value = '';
    this.count = 1;
  }

  focus(): void {
    this.price.focus();
  }

  private caption(text: string): HTMLElement {
    const caption = document.createElement('span');
    caption.className = 'auction__caption';
    caption.textContent = uiText(text);
    return caption;
  }

  private most(): number {
    return Math.max(1, Math.min(MAX_QUANTITY, this.entry?.item.quantity ?? 1));
  }

  private renderCard(template: ItemTemplate): void {
    const item = this.entry?.item;
    if (item === undefined) return;
    const parts = itemRow(
      template,
      this.desk.icons,
      upgradedName(template, item.templateId, item.upgradeLevel),
    );
    parts.row.classList.add('auction__row', 'auction__row--picked');
    markIcon(parts.icon, item.quantity, item.upgradeLevel);
    parts.meta.textContent =
      item.quantity > 1 ? uiFormat('{count} dans ton sac', { count: uiNumber(item.quantity) }) : '';
    const context = () => ({ wearer: this.desk.wearer(), details: {}, instance: item });
    describeOnHover(parts.slot, template, context);
    const change = document.createElement('button');
    change.type = 'button';
    change.className = 'smith__go auction__change';
    change.textContent = uiText('Changer');
    change.addEventListener('click', () => {
      this.options.onBack();
    });
    parts.actions.append(change);
    this.card.replaceChildren(parts.row);
  }

  private renderQuantity(): void {
    const most = this.most();
    this.quantityField.hidden = most === 1;
    this.quantityBox.replaceChildren(
      stepper({
        count: this.count,
        most,
        withMax: true,
        onStep: (next) => {
          this.count = Math.max(1, Math.min(most, next));
          this.renderQuantity();
          this.renderSummary();
        },
      }),
    );
  }

  private priceValue(): number | undefined {
    const price = Number(this.price.value);
    if (this.price.value === '' || !Number.isInteger(price)) return undefined;
    if (price < 1 || price > AUCTION_MAX_PRICE) return undefined;
    return price;
  }

  /** Price, the house's share and what comes back, then the button and why it waits. */
  private renderSummary(): void {
    const price = this.priceValue();
    this.each.textContent =
      price !== undefined && this.count > 1
        ? uiFormat('soit {each} l’unité', { each: formatGold(Math.round(price / this.count)) })
        : '';
    const line = (label: string, value: string, className = ''): HTMLElement => {
      const row = document.createElement('div');
      row.className = `auction__line ${className}`.trim();
      const name = document.createElement('span');
      name.textContent = label;
      const amount = document.createElement('span');
      amount.className = 'auction__line-value';
      amount.textContent = value;
      row.append(name, amount);
      return row;
    };
    const dash = '—';
    this.summary.replaceChildren(
      line(uiText('Prix du lot'), price === undefined ? dash : formatGold(price)),
      line(
        uiFormat('Commission {rate}', { rate: uiPercent(Math.round(AUCTION_FEE_RATE * 100)) }),
        price === undefined ? dash : `− ${formatGold(auctionFee(price))}`,
        'auction__line--fee',
      ),
      line(
        uiText('Tu recevras'),
        price === undefined ? dash : formatGold(sellerProceeds(price)),
        'auction__line--total',
      ),
    );

    const full = this.options.activeLots() >= AUCTION_MAX_LISTINGS;
    const typed = this.price.value !== '';
    let why: string | undefined;
    if (full) {
      why = uiFormat('Tu as déjà {max} lots en vente : retires-en un d’abord.', {
        max: AUCTION_MAX_LISTINGS,
      });
    } else if (price === undefined) {
      why = typed
        ? uiFormat('Indique un prix entre 1 et {max}.', { max: formatGold(AUCTION_MAX_PRICE) })
        : '';
    } else if (this.busy) why = '';
    const label =
      price === undefined
        ? uiText('Mettre en vente')
        : uiFormat('Mettre en vente pour {price}', { price: formatGold(price) });
    const action = workAction(label, why, () => {
      this.sell();
    });
    action.classList.add('auction__work');
    this.work.replaceChildren(action);
  }

  private sell(): void {
    const entry = this.entry;
    const price = this.priceValue();
    if (entry === undefined || price === undefined || this.busy) return;
    if (this.options.activeLots() >= AUCTION_MAX_LISTINGS) return;
    const quantity = this.count;
    this.busy = true;
    this.renderSummary();
    void this.desk
      .access()
      .then((ticket) =>
        listItem(this.desk.token, ticket, { itemId: entry.item.id, quantity, price }),
      )
      .then((listing) => {
        const name = uiText(getItem(listing.templateId)?.name ?? listing.templateId);
        const what = listing.quantity > 1 ? `${String(listing.quantity)} × ${name}` : name;
        this.desk.say(
          uiFormat('Mis en vente : {what} pour {price}.', {
            what,
            price: formatGold(listing.price),
          }),
          'success',
        );
        this.reset();
        this.options.onListed();
      })
      .catch((error: unknown) => {
        this.desk.say(uiText(failureText(error)), 'refused');
      })
      .finally(() => {
        this.busy = false;
        if (this.entry !== undefined) this.renderSummary();
      });
  }
}
